import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getAPIBaseURL } from '../../services/helpers'
import { getData, updateData } from '../../services/request'

const DEFAULT_MAINTENANCE_MESSAGE =
  'We are currently undergoing scheduled maintenance. Please check back shortly.'

const AdminEmploymeSettings = () => {
  const [jobPaymentConfig, setJobPaymentConfig] = useState({ paymentRequired: true })
  const [maintenanceConfig, setMaintenanceConfig] = useState({
    enabled: false,
    message: DEFAULT_MAINTENANCE_MESSAGE
  })
  const [loading, setLoading] = useState(true)
  const [savingPayment, setSavingPayment] = useState(false)
  const [savingMaintenance, setSavingMaintenance] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const fetchConfig = async () => {
    setLoading(true)
    setError('')
    const [paymentRes, maintenanceRes] = await Promise.all([
      getData(`${getAPIBaseURL()}/employme/admin/settings/job-payment`, {}, {}),
      getData(`${getAPIBaseURL()}/employme/admin/settings/maintenance`, {}, {})
    ])
    setLoading(false)

    if (paymentRes.error === '' && paymentRes.result?.status === 200) {
      const data = paymentRes.result.data?.data || paymentRes.result.data
      setJobPaymentConfig({
        paymentRequired: data?.paymentRequired !== false
      })
    } else {
      setError(
        paymentRes.result?.data?.message ||
          paymentRes.result?.data?.data ||
          'Unable to load settings'
      )
    }

    if (maintenanceRes.error === '' && maintenanceRes.result?.status === 200) {
      const data = maintenanceRes.result.data?.data || maintenanceRes.result.data
      setMaintenanceConfig({
        enabled: data?.enabled === true,
        message: data?.message || DEFAULT_MAINTENANCE_MESSAGE
      })
    }
  }

  useEffect(() => {
    fetchConfig()
  }, [])

  const handleSavePayment = async () => {
    setSavingPayment(true)
    setError('')
    setSuccess('')
    const headers = { 'Content-Type': 'application/json' }
    const res = await updateData(
      `${getAPIBaseURL()}/employme/admin/settings/job-payment`,
      headers,
      jobPaymentConfig
    )
    setSavingPayment(false)
    if (res.error === '' && res.result?.status === 200) {
      setSuccess('Payment settings saved successfully')
      if (res.result?.data?.data) setJobPaymentConfig(res.result.data.data)
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Failed to save')
    }
  }

  const handleSaveMaintenance = async () => {
    setSavingMaintenance(true)
    setError('')
    setSuccess('')
    const headers = { 'Content-Type': 'application/json' }
    const res = await updateData(
      `${getAPIBaseURL()}/employme/admin/settings/maintenance`,
      headers,
      maintenanceConfig
    )
    setSavingMaintenance(false)
    if (res.error === '' && res.result?.status === 200) {
      setSuccess(
        maintenanceConfig.enabled
          ? 'Maintenance mode enabled. The public site will show the maintenance page.'
          : 'Maintenance mode disabled. The site is live again.'
      )
      if (res.result?.data?.data) setMaintenanceConfig(res.result.data.data)
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Failed to save')
    }
  }

  return (
    <section className="mx-auto max-w-2xl">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employ Me settings</h1>
          <p className="text-sm text-gray-600">Configure job posting and platform behavior</p>
        </div>
        <Link
          to="/admin/app/employme"
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Back to overview
        </Link>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {success}
        </div>
      )}

      <div className="space-y-6">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          {loading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50/50 p-4">
                <div className="pr-4">
                  <h3 className="font-semibold text-gray-900">Maintenance mode</h3>
                  <p className="text-sm text-gray-600">
                    When enabled, every public page redirects to the maintenance screen. Admin
                    login and the admin panel stay available so you can turn it off.
                  </p>
                </div>
                <label className="relative inline-flex shrink-0 cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={maintenanceConfig.enabled}
                    onChange={(e) =>
                      setMaintenanceConfig((prev) => ({ ...prev, enabled: e.target.checked }))
                    }
                    className="peer sr-only"
                  />
                  <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-amber-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-4 peer-focus:ring-amber-200"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {maintenanceConfig.enabled ? 'On' : 'Off'}
                  </span>
                </label>
              </div>

              {maintenanceConfig.enabled && (
                <div>
                  <label
                    htmlFor="maintenance-message"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Message shown to visitors
                  </label>
                  <textarea
                    id="maintenance-message"
                    rows={3}
                    value={maintenanceConfig.message}
                    onChange={(e) =>
                      setMaintenanceConfig((prev) => ({ ...prev, message: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500"
                  />
                </div>
              )}

              <button
                onClick={handleSaveMaintenance}
                disabled={savingMaintenance}
                className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {savingMaintenance ? 'Saving...' : 'Save maintenance settings'}
              </button>
            </div>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          {loading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Require payment for job posts</h3>
                  <p className="text-sm text-gray-600">
                    When enabled, employers must pay before a job is reviewed and published. When
                    disabled, employers can submit jobs for free; approved jobs go live automatically
                    after AI review.
                  </p>
                </div>
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={jobPaymentConfig.paymentRequired}
                    onChange={(e) =>
                      setJobPaymentConfig((prev) => ({
                        ...prev,
                        paymentRequired: e.target.checked
                      }))
                    }
                    className="peer sr-only"
                  />
                  <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-gray-800 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-4 peer-focus:ring-gray-300"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {jobPaymentConfig.paymentRequired ? 'On' : 'Off'}
                  </span>
                </label>
              </div>

              {!jobPaymentConfig.paymentRequired && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  Free posting is active. New jobs skip Bachs payment and are sent straight to AI
                  moderation.
                </p>
              )}

              <button
                onClick={handleSavePayment}
                disabled={savingPayment}
                className="rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-900 disabled:opacity-50"
              >
                {savingPayment ? 'Saving...' : 'Save payment settings'}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default AdminEmploymeSettings
